import { fetchAuthSession } from '@aws-amplify/auth';

// API configuration
// export const API_URL = "https://rk6xxjbq90.execute-api.us-east-2.amazonaws.com/prod";
export const API_URL = "https://api.drafter.usefulchaos.com";

// Helper to get the current auth token
export const getAuthToken = async () => {
    const session = await fetchAuthSession();
    return `Bearer ${session.tokens.idToken.toString()}`;
};

// Thrown when a save loses the optimistic-concurrency race: another browser
// saved the draft since we last loaded it.
export class DraftConflictError extends Error {
    constructor() {
        super('Draft was modified by another session');
        this.name = 'DraftConflictError';
    }
}

// Build an error that carries the HTTP status and the API's error body so
// failures in the console say what actually went wrong server-side.
export const httpError = async (action, response) => {
    let detail = '';
    try {
        detail = await response.text();
    } catch {
        // Body unavailable; the status alone still helps.
    }
    return new Error(`Failed to ${action} (HTTP ${response.status})${detail ? `: ${detail}` : ''}`);
};

// API service functions
export const saveDraft = async (username, draftName, draftState, baseEtag) => {
    const token = await getAuthToken();
    const response = await fetch(`${API_URL}/save`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': token
        },
        body: JSON.stringify({
            user_id: username,
            draft_name: draftName,
            draft_data: draftState,
            ...(baseEtag ? { base_etag: baseEtag } : {})
        })
    });

    if (response.status === 409) {
        throw new DraftConflictError();
    }
    if (!response.ok) {
        throw await httpError('save draft', response);
    }

    return response.json();
};

export const loadDraft = async (username, draftName, knownEtag) => {
    const token = await getAuthToken();
    const etagParam = knownEtag ? `&etag=${encodeURIComponent(knownEtag)}` : '';
    const response = await fetch(
        `${API_URL}/load?user_id=${username}&draft_name=${draftName}${etagParam}`,
        {
            headers: {
                'Authorization': token
            }
        }
    );

    if (!response.ok) {
        throw await httpError('load draft', response);
    }

    return response.json();
};

export const listDrafts = async (username) => {
    const token = await getAuthToken();
    const response = await fetch(`${API_URL}/list?user_id=${username}`, {
        headers: {
            'Authorization': token
        }
    });

    if (!response.ok) {
        throw await httpError('load drafts', response);
    }

    return response.json();
};

export const deleteDraft = async (username, draftName) => {
    const token = await getAuthToken();
    const response = await fetch(`${API_URL}/delete`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': token
        },
        body: JSON.stringify({
            user_id: username,
            draft_name: draftName
        })
    });

    if (!response.ok) {
        throw await httpError('delete draft', response);
    }
    return response.json();
};